import { differenceInCalendarDays, parseISO } from 'date-fns';
import type { OfflineBundle } from './offline/domainTypes';
import type { Camada } from './types';

export function historicalLosses(
  bundle: OfflineBundle | null,
  camada: Pick<Camada, 'id' | 'sexo' | 'fase' | 'fecha_entrada'>,
  nave: number,
): { camadas: number; byAge: Map<number, { value: number; count: number }> } | null {
  if (
    !bundle?.complete ||
    ['camadas', 'lotes', 'registros'].some((key) => bundle.unavailable.includes(key))
  )
    return null;
  if (
    !bundle.tables.camadas.some(
      (c) =>
        c.id === camada.id &&
        c.sexo === camada.sexo &&
        c.fase === camada.fase &&
        c.fecha_entrada === camada.fecha_entrada,
    ) ||
    !bundle.tables.naves.some((n) => n.id === nave)
  )
    return null;
  const anteriores = new Map(
    bundle.tables.camadas
      .filter(
        (c) =>
          c.id !== camada.id &&
          c.cerrada === true &&
          c.sexo === camada.sexo &&
          c.fase === camada.fase &&
          String(c.fecha_entrada) < camada.fecha_entrada,
      )
      .map((c) => [c.id, c]),
  );
  const lotes = new Map(
    bundle.tables.lotes
      .filter((l) => l.nave_id === nave && anteriores.has(Number(l.camada_id)))
      .map((l) => [l.id, l]),
  );
  // Una camada contribuye una vez por edad; un día sin parte no es cero.
  const porEdad = new Map<number, Map<number, number>>();
  const contribuyentes = new Set<number>();
  for (const r of bundle.tables.registros) {
    const lote = lotes.get(Number(r.lote_nave_id));
    const anterior = lote && anteriores.get(Number(lote.camada_id));
    if (!anterior || (anterior.fecha_cierre && String(r.fecha) > String(anterior.fecha_cierre)))
      continue;
    const edad = differenceInCalendarDays(
      parseISO(String(r.fecha)),
      parseISO(String(anterior.fecha_entrada)),
    );
    const bajas = Number(r.bajas_muerte) + Number(r.bajas_triaje);
    if (!Number.isFinite(edad) || edad < 0 || !Number.isFinite(bajas) || bajas < 0) continue;
    const valores = porEdad.get(edad) ?? new Map<number, number>();
    const id = Number(anterior.id);
    valores.set(id, (valores.get(id) ?? 0) + bajas);
    porEdad.set(edad, valores);
    contribuyentes.add(id);
  }
  return {
    camadas: contribuyentes.size,
    byAge: new Map(
      Array.from(porEdad, ([edad, valores]) => [
        edad,
        {
          value: Array.from(valores.values()).reduce((sum, value) => sum + value, 0) / valores.size,
          count: valores.size,
        },
      ]),
    ),
  };
}
